import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Customer } from '@/models/customer';
import { Product } from '@/models/product';
import { CreateInvoiceData } from '@/models/invoice';
import { CustomerService } from '@/services/customerService';
import { ProductService } from '@/services/productService';
import { BillService } from '@/services/invoiceService';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import InvoiceReview from '@/components/InvoiceReview/InvoiceReview';
import SmartCustomerForm from '@/components/SmartCustomerForm/SmartCustomerForm';
import SmartProductForm from '@/components/SmartProductForm/SmartProductForm';
import SmartProductsList from '@/components/SmartProductsList/SmartProductsList';
import SmartInvoiceDetails from '@/components/SmartInvoiceDetails/SmartInvoiceDetails';
import SmartSummary from '@/components/SmartSummary/SmartSummary';
import { COMPANY_CONFIG } from '@/config/companyConfig';
import './CreateInvoicePage.css';

interface CreateInvoicePageProps {
  user: any;
}

const CreateInvoicePage: React.FC<CreateInvoicePageProps> = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if we're in edit mode
  const editInvoiceId = searchParams.get('edit');
  const isEditMode = Boolean(editInvoiceId);
  
  // Form data
  const [formData, setFormData] = useState<CreateInvoiceData>({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    order_date: new Date().toISOString().split('T')[0],
    transport: '',
    customer: '',
    customer_name: '',
    products: [],
    bill_amount: 0,
    discount: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    packing_charges: 0,
    round_off: 0,
    gross_amount: 0,
    total_amount: 0,
    status: 'draft',
    note: '',
    created_by: user?.username || 'ADMIN',
    hsn_list: [],
  });

  // State management
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // API loading states for section-level feedback
  const [invoiceDetailsLoading, setInvoiceDetailsLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);

  // UI State
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [loadedInvoiceData, setLoadedInvoiceData] = useState<any>(null);
  const [draftId, setDraftId] = useState<string | null>(null);

  const isDuplicateRequestError = (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error ?? '');
    return message.includes('Duplicate request cancelled') || message.includes('duplicate request') || message.includes('canceled');
  };

  // Load invoice data for edit mode
  const loadInvoiceData = async (invoiceId: string) => {
    setInvoiceDetailsLoading(true);
    try {
      console.log('Loading invoice data for ID:', invoiceId);
      const response = await BillService.getBillById(invoiceId);
      console.log('API Response:', response);
      
      if (response.success && response.data) {
        const invoice = response.data;
        console.log('Raw invoice data from API:', invoice);
        
        // Convert invoice data to form data format
        const invoiceFormData: CreateInvoiceData = {
          invoice_number: invoice.invoice_number || '',
          invoice_date: invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          order_date: invoice.order_date ? new Date(invoice.order_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          transport: invoice.transport || '',
          // Fix: Extract customer ID from customer object
          customer: typeof invoice.customer === 'object' ? invoice.customer._id : invoice.customer || '',
          customer_name: typeof invoice.customer === 'object' ? invoice.customer.name : (invoice as any).customer_name || '',
          // Fix: Convert ProductInfo[] to the expected format
          products: invoice.products ? invoice.products.map(productInfo => ({
            product: typeof productInfo.product === 'object' ? productInfo.product._id : productInfo.product,
            quantity: productInfo.quantity,
            poNumber: productInfo.poNumber || 0
          })) : [],
          bill_amount: invoice.bill_amount || 0,
          discount: invoice.discount || 0,
          cgst: invoice.cgst || 0,
          sgst: invoice.sgst || 0,
          igst: invoice.igst || 0,
          packing_charges: invoice.packing_charges || 0,
          round_off: invoice.round_off || 0,
          gross_amount: invoice.gross_amount || 0,
          total_amount: invoice.total_amount || 0,
          status: invoice.status || 'draft',
          note: invoice.note || '',
          created_by: invoice.created_by || user?.username || 'ADMIN',
          hsn_list: invoice.hsn_list ? invoice.hsn_list.map(hsnItem => ({
            hsn_code: hsnItem.hsn_code || '',
            quantity: hsnItem.quantity || 0,
            cgst: hsnItem.cgst || 0,
            sgst: hsnItem.sgst || 0,
            igst: hsnItem.igst || 0,
            total_amount: hsnItem.total_amount || 0,
            // Handle backward compatibility: calculate if missing
            total_taxable_amount: hsnItem.total_taxable_amount ?? 0, // Will be recalculated by calculateTotals
            total_gst: hsnItem.total_gst ?? ((hsnItem.cgst || 0) + (hsnItem.sgst || 0) + (hsnItem.igst || 0))
          })) : [],
        };
        
        setFormData(invoiceFormData);
        setLoadedInvoiceData(invoice);
        
        console.log('Invoice data loaded:', {
          invoice,
          invoiceFormData,
          customerId: invoice.customer,
          productsCount: invoice.products?.length || 0,
          products: invoice.products,
          mappedProducts: invoiceFormData.products
        });
        
        return invoice;
      } else {
        showNotification('Failed to load invoice data', 'error');
        return null;
      }
    } catch (error) {
      if (isDuplicateRequestError(error)) {
        return null;
      }
      console.error('Error loading invoice data:', error);
      showNotification('Failed to load invoice data', 'error');
      return null;
    } finally {
      setInvoiceDetailsLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadCustomers(),
          loadProducts(),
          !isEditMode ? loadNextInvoiceNumber() : Promise.resolve()
        ]);
      } catch (error) {
        console.error('Error loading initial data:', error);
        showNotification('Failed to load initial data', 'error');
      } finally {
        setLoading(false);
      }

      if (isEditMode && editInvoiceId) {
        loadInvoiceData(editInvoiceId);
      } else {
        // If not in edit mode, try to load draft in the background and keep the page interactive.
        try {
          const response = await BillService.getBills();
          if (response.success && Array.isArray(response.data)) {
            const drafts = response.data.filter(b => b.status === 'draft' && b.created_by === (user?.username || ''));
            if (drafts.length > 0) {
              const draft = drafts[drafts.length - 1];
              if (draft && draft._id) {
                const loaded = await loadInvoiceData(draft._id);
                if (loaded && loaded._id) {
                  setDraftId(loaded._id);
                }
              }
            } else if (!selectedCustomer && customers.length > 0) {
              const first = customers[0];
              setSelectedCustomer(first);
              setFormData(prev => ({ ...prev, customer: first._id, customer_name: first.name }));
            }
          }
        } catch (e) {
          if (isDuplicateRequestError(e)) {
          } else {
            console.warn('Failed to load draft from server', e);
          }
          if (!selectedCustomer && customers.length > 0) {
            const first = customers[0];
            setSelectedCustomer(first);
            setFormData(prev => ({ ...prev, customer: first._id, customer_name: first.name }));
          }
        }
      }
    };

    loadInitialData();
  }, [isEditMode, editInvoiceId]);

  // Handle customer selection after customers are loaded (for edit mode)
  useEffect(() => {
    console.log('Customer selection useEffect triggered:', {
      loadedInvoiceData: !!loadedInvoiceData,
      customersLength: customers.length,
      customerId: loadedInvoiceData?.customer,
      formDataCustomer: formData.customer
    });
    
    if (loadedInvoiceData && customers.length > 0) {
      // Try to get customer ID from either the loaded invoice data or form data
      let customerId = '';
      if (typeof loadedInvoiceData.customer === 'object') {
        customerId = loadedInvoiceData.customer._id;
      } else if (typeof loadedInvoiceData.customer === 'string') {
        customerId = loadedInvoiceData.customer;
      } else if (formData.customer) {
        customerId = formData.customer;
      }
      
      console.log('Looking for customer with ID:', customerId);
      
      if (customerId) {
        const customer = customers.find(c => c._id === customerId);
        console.log('Found customer:', customer);
        if (customer) {
          setSelectedCustomer(customer);
          console.log('Customer set successfully');
        }
      }
    }
  }, [loadedInvoiceData, customers, formData.customer]);

  // Load customers
  const loadCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await CustomerService.getCustomers();
      if (response.success) {
        setCustomers(response.data);
      } else {
        showNotification('Failed to load customers', 'error');
      }
    } catch (error) {
      if (isDuplicateRequestError(error)) {
      } else {
        console.error('Error loading customers:', error);
        showNotification('Failed to load customers', 'error');
      }
    } finally {
      setCustomersLoading(false);
    }
  };

  // When customers are available, if not edit mode, try to restore draft customer or default to first
  useEffect(() => {
    if (!isEditMode && customers.length > 0) {
      // If no invoice was loaded from server (draft) and no customer selected, default to the first
      if (!loadedInvoiceData && !selectedCustomer) {
        const first = customers[0];
        setSelectedCustomer(first);
        setFormData(prev => ({ ...prev, customer: first._id, customer_name: first.name }));
      }
    }
  }, [customers]);

  // Server-side draft handling
  const saveDraftToServer = async (data: CreateInvoiceData) => {
    try {
      const payload = {
        ...data,
        customer: data.customer || selectedCustomer?._id,
        customer_name: data.customer_name || selectedCustomer?.name || ''
      };

      if (isEditMode) {
        if (!editInvoiceId) return;
        setDraftId(editInvoiceId);
        await BillService.updateBill(editInvoiceId, payload as any);
        return;
      }

      // If we don't yet have a draft id, only attempt to create a server draft
      // when the minimum required fields are present to satisfy the backend.
      // Backend requires: invoice_number, invoice_date, customer, products (non-empty), bill_amount, gross_amount
      if (!draftId) {
        const hasProducts = Array.isArray(payload.products) && payload.products.length > 0;
        const hasCustomer = !!payload.customer;

        if (!hasProducts || !hasCustomer) {
          // Skip creating draft on server until we have at least one product and a customer
          return;
        }

        // Ensure invoice_number and invoice_date exist; generate simple defaults if missing
        if (!payload.invoice_number) {
          const nextResp = await BillService.getNextBillNumber();
          if (nextResp.success && nextResp.data?.nextNumber) {
            payload.invoice_number = nextResp.data.nextNumber;
          } else {
            payload.invoice_number = `1/${new Date().getFullYear().toString().slice(-2)}-${(new Date().getFullYear()+1).toString().slice(-2)}`;
          }
        }
        if (!payload.invoice_date) {
          payload.invoice_date = new Date().toISOString().split('T')[0];
        }

        // Ensure numeric totals are present (calculateTotals should have run, but guard here)
        payload.bill_amount = payload.bill_amount || 0;
        payload.gross_amount = payload.gross_amount || payload.bill_amount;

        const resp = await BillService.createBill(payload as any);
        if (resp.success && resp.data && resp.data._id) {
          setDraftId(resp.data._id);
        }
      } else {
        // If we already have a draft id, update it with whatever fields are present
        await BillService.updateBill(draftId, payload as any);
      }
    } catch (e) {
      console.warn('Failed to save draft to server', e);
    }
  };

  const clearDraft = () => {
    // For server-side drafts we simply clear the local draftId reference.
    setDraftId(null);
  };

  // Debounced draft saver to avoid rapid repeated API calls while typing
  const draftSaveTimerRef = React.useRef<number | null>(null);
  const scheduleSaveDraft = (data: CreateInvoiceData, delay = 800) => {
    try {
      if (draftSaveTimerRef.current) {
        window.clearTimeout(draftSaveTimerRef.current);
      }
    } catch (e) {}

    // schedule
    draftSaveTimerRef.current = window.setTimeout(() => {
      saveDraftToServer(data);
      draftSaveTimerRef.current = null;
    }, delay) as unknown as number;
  };

  // Load products
  const loadProducts = async (force: boolean = false) => {
    setProductsLoading(true);
    try {
      const response = await ProductService.getProductsForSelect(force);
      if (response.success) {
        setProducts(response.data);
      } else {
        showNotification('Failed to load products', 'error');
      }
      setProductsLoading(false);
    } catch (error) {
      if (isDuplicateRequestError(error)) {
        setProductsLoading(false);
        return;
      }
      console.error('Error loading products:', error);
      // Retry once after a short delay to handle transient failures
      showNotification('Failed to load products, retrying...', 'warning');
      setTimeout(async () => {
        try {
          const retryResp = await ProductService.getProductsForSelect(true);
          if (retryResp.success) {
            setProducts(retryResp.data);
            showNotification('Products loaded', 'success');
          } else {
            showNotification('Failed to load products', 'error');
          }
        } catch (e) {
          console.error('Retry failed loading products', e);
          showNotification('Failed to load products', 'error');
        } finally {
          setProductsLoading(false);
        }
      }, 1000);
    }
  };

  // Load next invoice number
  const loadNextInvoiceNumber = async () => {
    try {
      const response = await BillService.getNextBillNumber();
      if (response.success) {
        setFormData(prev => ({
          ...prev,
          invoice_number: response.data.nextNumber
        }));
      }
    } catch (error) {
      if (!isDuplicateRequestError(error)) {
        console.error('Error loading next invoice number:', error);
      }
    }
  };

  // Show notification
  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };


  // Handle customer selection
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    
    setFormData(prev => ({
      ...prev,
      customer: customer._id,
      customer_name: customer.name
    }));

    // Clear customer error
    if (formErrors.customer) {
      setFormErrors(prev => ({
        ...prev,
        customer: ''
      }));
    }
  };

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const productsTotal = formData.products.reduce((sum, productInfo) => {
      const product = products.find(p => p._id === productInfo.product);
      if (product) {
        return sum + (product.rate * productInfo.quantity);
      }
      return sum;
    }, 0);

    const discount = formData.discount || 0;
    const billAmount = productsTotal - discount;
    
    // Calculate GST based on customer type
    const isLocal = selectedCustomer?.localoutside?.toLowerCase() === 'local';
    const gstPercentage = formData.products.reduce((sum, productInfo) => {
      const product = products.find(p => p._id === productInfo.product);
      if (product) {
        return sum + (product.gst_percentage * productInfo.quantity);
      }
      return sum;
    }, 0) / Math.max(formData.products.reduce((sum, productInfo) => sum + productInfo.quantity, 0), 1);

    const cgst = isLocal ? (billAmount * gstPercentage) / 200 : 0;
    const sgst = isLocal ? (billAmount * gstPercentage) / 200 : 0;
    const igst = !isLocal ? (billAmount * gstPercentage) / 100 : 0;
    
    const packingCharges = formData.packing_charges || 0;
    const grossAmount = billAmount + cgst + sgst + igst + packingCharges;
    const roundOff = Math.round(grossAmount) - grossAmount;
    const finalTotal = grossAmount + roundOff;

    // Generate HSN list
    const hsnMap = new Map<string, { hsn_code: string; quantity: number; cgst: number; sgst: number; igst: number; total_amount: number; total_taxable_amount: number; total_gst: number }>();
    formData.products.forEach(productInfo => {
      const product = products.find(p => p._id === productInfo.product);
      if (product) {
        const hsnCode = product.hsn_code.toString();
        const existing = hsnMap.get(hsnCode);
        const productTotal = product.rate * productInfo.quantity;
        const productGst = (productTotal * product.gst_percentage) / 100;
        
        if (existing) {
          existing.quantity += productInfo.quantity;
          existing.total_taxable_amount += productTotal;
          if (isLocal) {
            existing.cgst += productGst / 2;
            existing.sgst += productGst / 2;
          } else {
            existing.igst += productGst;
          }
          // Update total_gst as total GST amount (cgst + sgst + igst)
          existing.total_gst = existing.cgst + existing.sgst + existing.igst;
          // Update total_amount as sum of taxable amount and GST amount
          existing.total_amount = existing.total_taxable_amount + existing.total_gst;
        } else {
          const cgstAmount = isLocal ? productGst / 2 : 0;
          const sgstAmount = isLocal ? productGst / 2 : 0;
          const igstAmount = !isLocal ? productGst : 0;
          const totalGst = cgstAmount + sgstAmount + igstAmount;
          hsnMap.set(hsnCode, {
            hsn_code: hsnCode,
            quantity: productInfo.quantity,
            cgst: cgstAmount,
            sgst: sgstAmount,
            igst: igstAmount,
            total_taxable_amount: productTotal, // Base amount before GST
            total_gst: totalGst, // Total GST amount
            total_amount: productTotal + totalGst // Sum of taxable amount and GST amount
          });
        }
      }
    });

    setFormData(prev => ({
      ...prev,
      bill_amount: productsTotal,
      cgst,
      sgst,
      igst,
      gross_amount: finalTotal,
      round_off: roundOff,
      total_amount: finalTotal,
      hsn_list: Array.from(hsnMap.values())
    }));
  }, [formData.products, formData.discount, formData.packing_charges, products, selectedCustomer]);

  // Recalculate totals when dependencies change
  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  // Debug: Log current state
  useEffect(() => {
    console.log('Current state:', {
      isEditMode,
      editInvoiceId,
      selectedCustomer: selectedCustomer?.name,
      formDataProducts: formData.products?.length || 0,
      customersCount: customers.length,
      productsCount: products.length
    });
  }, [isEditMode, editInvoiceId, selectedCustomer, formData.products, customers.length, products.length]);

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Invoice details validation
    if (!formData.invoice_number.trim()) {
      errors.invoice_number = 'Invoice number is required';
    }
    if (!formData.invoice_date) {
      errors.invoice_date = 'Invoice date is required';
    }
    if (!formData.order_date) {
      errors.order_date = 'Order date is required';
    }

    // Customer validation
    if (!selectedCustomer) {
      errors.customer = 'Please select a customer';
    }

    // Products validation
    if (formData.products.length === 0) {
      errors.products = 'At least one product is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save invoice
  const handleSave = async () => {
    if (!validateForm()) {
      showNotification('Please fix the errors before saving', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const invoiceData = {
        ...formData,
        customer: selectedCustomer!._id,
        customer_name: selectedCustomer!.name
      };

      let response;
      if (isEditMode && editInvoiceId) {
        // Update existing invoice
        response = await BillService.updateBill(editInvoiceId, invoiceData);
        if (response.success) {
          showNotification('Invoice updated successfully!', 'success');
          // Clear any saved draft after successful save
          clearDraft();
          setTimeout(() => {
            navigate('/invoices');
          }, 1500);
        } else {
          showNotification(response.message || 'Failed to update invoice', 'error');
        }
      } else {
        // Create new invoice
        if (draftId) {
          response = await BillService.updateBill(draftId, invoiceData as any);
        } else {
          response = await BillService.createBill(invoiceData);
        }
        if (response.success) {
          showNotification('Invoice created successfully!', 'success');
          // Clear saved draft when a new invoice is successfully created
          clearDraft();
          setTimeout(() => {
            navigate('/home');
          }, 1500);
        } else {
          showNotification(response.message || 'Failed to create invoice', 'error');
        }
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} invoice:`, error);
      showNotification(`Failed to ${isEditMode ? 'update' : 'create'} invoice`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Get validation status
  const getValidationStatus = (): { status: 'success' | 'error' | 'warning' | 'info'; message: string } => {
    const hasErrors = Object.values(formErrors).some(e => e !== '');
    const hasCustomer = selectedCustomer !== null;
    const hasProducts = formData.products.length > 0;
    const hasInvoiceDetails = formData.invoice_number && formData.invoice_date && formData.order_date;

    if (hasErrors) return { status: 'error', message: 'Please fix the errors' };
    if (!hasInvoiceDetails) return { status: 'warning', message: 'Complete invoice details' };
    if (!hasCustomer) return { status: 'warning', message: 'Select a customer' };
    if (!hasProducts) return { status: 'warning', message: 'Add products' };
    return { status: 'success', message: 'Ready to save' };
  };

  const validationStatus = getValidationStatus();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box className="create-invoice-page">
      {/* Page Banner */}
      <Box className="invoice-page-banner">
        <Container maxWidth="xl">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="inherit" sx={{ fontSize: 14 }} />}
            className="invoice-breadcrumbs"
          >
            <Link
              underline="hover"
              color="inherit"
              className="breadcrumb-link"
              onClick={() => navigate('/home')}
              sx={{ cursor: 'pointer' }}
            >
              Home
            </Link>
            <Link
              underline="hover"
              color="inherit"
              className="breadcrumb-link"
              onClick={() => navigate('/invoices')}
              sx={{ cursor: 'pointer' }}
            >
              Invoices
            </Link>
            <Typography variant="caption" className="breadcrumb-current">
              {isEditMode ? 'Edit Invoice' : 'New Invoice'}
            </Typography>
          </Breadcrumbs>
          <Typography variant="h5" className="invoice-page-title">
            {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
          </Typography>
          <Typography variant="body2" className="invoice-page-subtitle">
            {isEditMode
              ? formData.invoice_number
                ? `Editing invoice ${formData.invoice_number}`
                : 'Edit existing invoice details'
              : 'Generate a new invoice for your customer'}
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" className="create-invoice-container">
        {/* Main Form Grid */}
        <Grid container spacing={2} className="form-grid">
          {/* Invoice Details + Customer Row (70-30) */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {/* Invoice Details - 70% */}
              <Grid item xs={12} md={8}>
                <Box className="smart-form-section">
                  <SmartInvoiceDetails
                    formData={formData}
                    onFieldChange={(field, value) => {
                      setFormData(prev => {
                        const next = { ...prev, [field]: value } as CreateInvoiceData;
                        // Persist draft when invoice fields change (debounced)
                        scheduleSaveDraft(next);
                        return next;
                      });
                      if (formErrors[field]) {
                        setFormErrors(prev => ({ ...prev, [field]: '' }));
                      }
                    }}
                    errors={formErrors}
                    disabled={loading}
                    loading={invoiceDetailsLoading || loading}
                  />
                </Box>
              </Grid>

              {/* Customer Section - 30% */}
              <Grid item xs={12} md={4}>
                <Box className="smart-form-section">
                  <SmartCustomerForm
                    customers={customers}
                    selectedCustomer={selectedCustomer}
                    onCustomerSelect={handleCustomerSelect}
                    onCustomersRefresh={async () => await loadCustomers()}
                    disabled={loading}
                    loading={customersLoading || loading}
                    error={formErrors.customer}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Smart Product Form */}
          <Grid item xs={12}>
            <Box className="smart-form-section">
              <SmartProductForm
                products={products}
                onProductAdd={(productId, quantity, poNumber) => {
                  const newProduct = {
                    product: productId,
                    quantity,
                    poNumber
                  };
                  setFormData(prev => {
                    const next = {
                      ...prev,
                      products: [...prev.products, newProduct]
                    };
                    // Persist draft after adding (debounced)
                    scheduleSaveDraft(next);
                    return next;
                  });
                  setFormErrors(prev => ({ ...prev, products: '' }));
                }}
                onProductsRefresh={async () => await loadProducts(true)}
                disabled={loading}
                loading={productsLoading || loading}
                error={formErrors.products}
                user={user}
              />
            </Box>
          </Grid>

          {/* Products List + Summary Row (70-30) */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {/* Products List - 70% */}
              <Grid item xs={12} md={8}>
                <Box className="smart-form-section">
                  <SmartProductsList
                    products={formData.products}
                    allProducts={products}
                    onRemoveProduct={(index) => {
                      const updatedProducts = formData.products.filter((_, i) => i !== index);
                      const next = { ...formData, products: updatedProducts };
                      setFormData(next);
                      scheduleSaveDraft(next);
                    }}
                    onUpdateProduct={(index, updates) => {
                      const updatedProducts = [...formData.products];
                      updatedProducts[index] = {
                        ...updatedProducts[index],
                        quantity: updates.quantity ?? updatedProducts[index].quantity,
                        poNumber: updates.poNumber ?? updatedProducts[index].poNumber
                      };
                      const next = { ...formData, products: updatedProducts };
                      setFormData(next);
                      scheduleSaveDraft(next);
                      calculateTotals();
                    }}
                    onReorderProducts={(fromIndex, toIndex) => {
                      const updatedProducts = [...formData.products];
                      const [moved] = updatedProducts.splice(fromIndex, 1);
                      updatedProducts.splice(toIndex, 0, moved);
                      const next = { ...formData, products: updatedProducts };
                      setFormData(next);
                      scheduleSaveDraft(next);
                    }}
                    formatCurrency={formatCurrency}
                    disabled={loading}
                    loading={productsLoading || loading}
                  />
                </Box>
              </Grid>

              {/* Financial Summary - 30% */}
              <Grid item xs={12} md={4}>
                <Box className="smart-form-section">
                  <SmartSummary
                    formData={formData}
                    selectedCustomer={selectedCustomer}
                    onRefresh={calculateTotals}
                    disabled={loading}
                    loading={productsLoading || loading}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {/* Fixed Bottom Action Strip */}
      <Box className="fixed-action-strip">
        <Container maxWidth="xl">
          <Box className="action-strip-content">
            <Box className="action-strip-amount">
              <Typography variant="body2" className="action-strip-label">
                Total Amount
              </Typography>
              <Typography variant="h6" className="action-strip-value">
                {formatCurrency(formData.gross_amount)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || submitting || validationStatus.status === 'error'}
              startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
              className="action-strip-button"
              size="large"
            >
              {submitting ? 'Saving...' : isEditMode ? 'Update Invoice' : 'Save Invoice'}
            </Button>
          </Box>
        </Container>
      </Box>





      {/* Preview Dialog */}
      <Dialog
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        maxWidth="lg"
        fullWidth
        className="preview-dialog"
      >
        <DialogTitle className="dialog-title">
          Invoice Preview
          <IconButton
            onClick={() => setShowPreviewDialog(false)}
            className="close-button"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="dialog-content">
          <InvoiceReview
            formData={formData}
            selectedCustomer={selectedCustomer}
            companyConfig={COMPANY_CONFIG}
            products={products}
            onSave={handleSave}
            onBack={() => setShowPreviewDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateInvoicePage; 